// Copyright (c) Move Closer 2022.

import { Component, Mixins } from 'vue-property-decorator'
import { EventbusType, IEventbus } from '@movecloser/front-core'

import { AllowedAttributes, CartItemData, ISiteService, SiteServiceType } from '../../../contexts'
import { Inject, logger } from '../../../support'

import { BaseCartMixin, IBaseCart } from '../../checkout/shared/mixins/base-cart.mixin'
import { CartModel } from '../../checkout/contracts'
import { IProductsRepository, ProductsRepositoryType } from '../../products/contracts/repositories'
import { RouteName } from '../../checkout/routes'

import { IToastMixin, ToastMixin } from './toast.mixin'
import { CartAnalyticsMixin } from './cart-analytics.mixin'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl> (original)
 * @author Filip Rurak <filip.rurak@movecloser.pl> (edited)
 */
@Component<CartViewMixin>({
  name: 'CartViewMixin',
  mounted () {
    if (this.cart) {
      this.eventBus.emit('app:cart.view', this.getCartViewPayload(this.cart))
    }
  }
})
export class CartViewMixin extends Mixins<IToastMixin, IBaseCart, CartAnalyticsMixin>(
  ToastMixin,
  BaseCartMixin,
  CartAnalyticsMixin
) {
  @Inject(ProductsRepositoryType)
  protected readonly productsRepository!: IProductsRepository

  @Inject(EventbusType)
  protected readonly eventBus!: IEventbus

  @Inject(SiteServiceType)
  public readonly siteService!: ISiteService

  public applyingCoupon: boolean = false
  public couponCode: string = ''

  public isCartItemPending: boolean = false
  public shouldPreserveDeleted: boolean = false

  /**
   * Errors map.
   *
   * NOTE!: If there will be more than one action that should have different error handling
   * method, it is better to make it as enum as =>  EX: (ActionType.UpdateItems, etc...)
   */
  public errors: Record<string, string> = {
    updateItems: ''
  }

  public get freeDeliveryFrom (): number | null {
    return this.siteService?.getProperty('freeDeliveryFrom') as number | null
  }

  /**
   * Determines whether shipping methods is selected.
   */
  public get hasShipping (): boolean {
    return !!this.cart && !!this.cart.selectedShippingMethod
  }

  /**
   * Determines whether there are discounts.
   */
  public get hasDiscounts (): boolean {
    if (!this.cart) {
      return false
    }

    return Array.isArray(this.cart.getDiscounts()) && this.cart.getDiscounts().length > 0
  }

  /**
   * Determines whether cart has items.
   */
  public get hasCartItems (): boolean {
    if (!this.cart) {
      return false
    }

    return this.cart.hasItems()
  }

  /**
   * Determines
   */
  public get hasCoupons (): boolean {
    if (!this.cart) {
      return false
    }

    return Array.isArray(this.cart.getCoupons()) && this.cart.getCoupons().length > 0
  }

  public get isCartReady (): boolean {
    return this.cart !== null
  }

  /**
   * Determines the price of the shipping.
   */
  public get shipping (): number {
    return this.cart?.getShippingPrice() ?? 0
  }

  public get toFreeDelivery (): { value: number; range: number } {
    if (!this.cart || !this.freeDeliveryFrom) {
      return { value: 0, range: 100 }
    }

    return {
      value: Number(Math.max(0, this.freeDeliveryFrom - this.cart.getTotalPrice())
        .toFixed(2)),
      range: (this.cart.getTotalPrice() / this.freeDeliveryFrom) * 100
    }
  }

  /**
   * Applies coupon code to cart.
   */
  public async applyCoupon (): Promise<void> {
    try {
      if (this.couponCode.length < 1) {
        return
      }

      this.applyingCoupon = true

      const updatedCart = await this.cartService.applyCoupon(this.cartId, this.couponCode)

      this.refreshCart(this.considerPreservingItems(updatedCart))
    } catch (e) {
      logger(e, 'warn')
      this.showToast((e as Error).message, 'danger')
    } finally {
      this.applyingCoupon = false
    }
  }

  public async clearCoupons (): Promise<void> {
    try {
      const updatedCart = await this.cartService.clearCoupons(this.cartId)

      this.refreshCart(this.considerPreservingItems(updatedCart))
    } catch (e) {
      logger(e, 'warn')
      this.showToast((e as Error).message, 'danger')
    }
  }

  /**
   * Handles isLoading event in cart item component.
   */
  public onIsLoadingHandler (value: boolean): void {
    this.isCartItemPending = value
  }

  /**
   * Handles quantity changing of particular product.
   */
  public async onQuantityChange (uid: string, newQuantity: number, previousQuantity: number): Promise<void> {
    const item = this.cart?.items.find(item => item.uid === uid)
    if (item) {
      const gifts = Object.values(item.product.variants)[0].attributes[AllowedAttributes.GiftsSku] as string[]
      if (newQuantity === 1 && gifts && gifts.length > 0) {
        /**
         * Emit event if product (that adds some gift) with quantity '0' has been restored
         */
        this.eventBus.emit('app:cart.gift-item-valid-quantity', { gifts, uid, newQuantity, previousQuantity })
      }
    }

    try {
      let updatedCart: CartModel

      if (this.isAddedBack(uid)) {
        const itemToAdd = this.cart?.items.find(item => {
          return item.uid === uid
        })

        if (!itemToAdd) {
          throw new Error(`Item with uid ${uid} not found in cart`)
        }

        const sku = this.getFirstVariant(itemToAdd).sku

        const temp = await this.cartService.addToCart(this.cartId, sku, newQuantity)
        updatedCart = temp
        updatedCart = this.updateAfterAddingBack(updatedCart, uid, sku)

        if (temp.items.length !== updatedCart.items.length) {
          updatedCart = temp
        }
      } else {
        const temp = await this.cartService.updateCartItem(this.cartId, uid, newQuantity)
        updatedCart = temp
        updatedCart = this.considerPreservingItems(updatedCart, uid, newQuantity)

        this.eventBus.emit('app:cart.updated-item-quantity', { uid, previousQuantity, newQuantity, valid: true })

        if (temp.items.length !== updatedCart.items.length) {
          updatedCart = temp
        }
      }

      this.eventBus.emit('app:cart.view', this.getCartViewPayload(updatedCart))
      this.refreshCart(updatedCart)
    } catch (e) {
      logger(e, 'warn')
      this.eventBus.emit('app:cart.updated-item-quantity', { uid, previousQuantity, newQuantity, valid: false })
      this.errors.updateItems = (e as Error).message
      this.showToast((e as Error).message, 'danger')
    }
  }

  /**
   * Determines whether given action has error.
   */
  public hasError (action: string): boolean {
    return Object.prototype.hasOwnProperty.call(this.errors, action) &&
      this.errors[action].length > 0
  }

  /**
   * Gets the action error.
   */
  public getError (action: string): string {
    if (!this.hasError(action)) {
      return ''
    }

    return this.errors[action]
  }

  public goToCheckout (): void {
    this.$router.push({ name: `checkout.${RouteName.Checkout}` })
  }

  public goToHome (): void {
    this.$router.push('/')
  }

  /**
   * Handles @removeFromCartCb of SingleCartItem organism.
   */
  public async removeFromCart (uid: string): Promise<void> {
    try {
      const itemToRemove = this.cart?.items.find(item => {
        return item.uid === uid
      })

      const updatedCart = await this.cartService.removeFromCart(this.cartId, uid)

      if (itemToRemove) {
        this.eventBus.emit('app:cart.remove', this.getCartRemovePayload(itemToRemove, updatedCart))
      }

      this.refreshCart(this.considerPreservingItems(updatedCart, uid, 0))
    } catch (e) {
      logger(e, 'warn')
      this.showToast((e as Error).message, 'danger')
    }
  }

  private considerPreservingItems (cart: CartModel, uid?: string, quantity?: number): CartModel {
    if (!this.shouldPreserveDeleted) {
      return cart
    }

    /** Retrieved sample items from updated cart */
    const samples = cart.items.filter((item) => item[AllowedAttributes.IsSample])
    const gifts = cart.items.filter((item) => item[AllowedAttributes.IsGift])

    let items = this.cart?.items.map(item => {
      if (item.uid === uid) {
        if (quantity === 0 && item.outOfStock) {
          return null
        }
        return { ...item, quantity: quantity ?? 0 }
      }
      return item
    }) ?? []

    items = items.filter(item => item !== null)

    if (cart.items.length !== this.cart?.items.length) {
      items = items
        .filter((item) => item && !item[AllowedAttributes.IsGift])
        .filter((item) => item && !item[AllowedAttributes.IsSample])
        .concat(gifts)
        .concat(samples)
    }
    return { ...cart, items: items as CartItemData[] }
  }

  private isAddedBack (uid: string): boolean {
    if (!this.shouldPreserveDeleted) {
      return false
    }

    const item = this.cart?.items.find(i => i.uid === uid)

    return !item || item.quantity === 0
  }

  private updateAfterAddingBack (cart: CartModel, uid: string, sku: string) {
    if (!this.cart) {
      return cart
    }

    const updatedItem = cart.items.find(item => Object.values(item.product.variants)[0].sku === sku)

    if (!updatedItem) {
      throw new Error('Updated item not found in cart')
    }

    const items = this.cart.items.map(item => (item.uid === uid) ? updatedItem : item)

    return { ...cart, items }
  }
}
